/**
 * WebAuthn / passkey helpers for biometric step-up (REQ-AUTH-005).
 *
 * IMPORTANT — why this is self-hosted, NOT Supabase:
 *   Supabase/GoTrue exposes passkeys only as a BETA passwordless PRIMARY login;
 *   it does NOT surface a WebAuthn MFA/AAL2 factor through the SDK. So we cannot
 *   drive step-up through supabase.auth.mfa.* for WebAuthn. Instead we run the
 *   WebAuthn ceremonies against ELREIGN's own backend (@simplewebauthn/server on
 *   Azure Functions), store credentials in Cosmos, and record the SAME
 *   Elreign-side step-up posture (app_metadata.mfa_challenge_at) the TOTP path
 *   records. The biometric NEVER leaves the device — only a public-key assertion
 *   reaches the server (POPIA: no server-side biometric matching).
 *
 * The browser half uses @simplewebauthn/browser (startRegistration /
 * startAuthentication) which wraps navigator.credentials.create/.get and
 * base64url-encodes the ceremony payloads for our JSON endpoints.
 */

import {
  startRegistration,
  startAuthentication,
  type PublicKeyCredentialCreationOptionsJSON,
  type PublicKeyCredentialRequestOptionsJSON,
  type AuthenticationResponseJSON,
} from '@simplewebauthn/browser';
import { api } from './api';

/**
 * Feature-detect the WebAuthn API. Returns false in jsdom/test environments
 * (no navigator.credentials) and on older browsers. Kept as a lightweight
 * navigator check so it is deterministic under test.
 */
export function isWebAuthnSupported(): boolean {
  if (typeof navigator === 'undefined') return false;
  const creds = (navigator as Navigator & { credentials?: CredentialsContainer }).credentials;
  if (!creds) return false;
  return typeof creds.create === 'function' && typeof creds.get === 'function';
}

/** A passkey summary as returned by GET /v1/auth/webauthn/credentials. */
export interface PasskeySummary {
  id: string;
  deviceName: string;
  deviceType: string | null;
  backedUp: boolean | null;
  createdAt: string;
  lastUsedAt: string | null;
}

interface CredentialsListResponse {
  credentials: PasskeySummary[];
}

/**
 * Enroll a new passkey: fetch server-generated registration options, run the
 * browser ceremony (biometric/PIN gesture), and post the attestation for
 * server-side verification + persistence. Runs on the OPERATOR's own session.
 */
export async function enrollPasskey(deviceName: string): Promise<void> {
  const optionsJSON = await api<PublicKeyCredentialCreationOptionsJSON>(
    '/api/v1/auth/webauthn/register/options',
    { method: 'POST', useOperatorSession: true },
  );
  const attestation = await startRegistration({ optionsJSON });
  await api('/api/v1/auth/webauthn/register', {
    method: 'POST',
    body: { deviceName, response: attestation },
    useOperatorSession: true,
  });
}

/**
 * Step-up with a passkey: fetch server-generated authentication options,
 * run the browser assertion (biometric/PIN gesture), and post it for
 * server-side verification. On success the backend stamps the SAME
 * mfa_challenge_at posture the TOTP step-up stamps — the caller must then
 * refreshSession() (done by useStepUpChallenge) for the claim to land.
 */
export async function stepUpWithPasskey(): Promise<void> {
  const optionsJSON = await api<PublicKeyCredentialRequestOptionsJSON>(
    '/api/v1/auth/webauthn/step-up/options',
    { method: 'POST', useOperatorSession: true },
  );
  const assertion = await startAuthentication({ optionsJSON });
  await api('/api/v1/auth/webauthn/step-up', {
    method: 'POST',
    body: { response: assertion },
    useOperatorSession: true,
  });
}

/**
 * Run a FRESH biometric gesture bound to ONE resolution-signing act and return
 * the WebAuthn assertion for the sign request to carry.
 *
 * Flow: fetch resolution-BOUND authentication options (the server persists a
 * single-use challenge keyed to this resolution id) → run the browser assertion
 * (Face ID / Touch ID / Windows Hello / fingerprint) → return the assertion JSON.
 * The sign endpoint (`POST /v1/resolutions/{id}/signatures`) verifies it
 * server-side and records it as strong ECTA s13 EVIDENCE — it never changes the
 * signature's legal class.
 *
 * Runs on the signer's OWN session (NOT the operator session): the signer is the
 * authenticated user, and the backend refuses act-as/impersonation for this
 * ceremony (C2 wall). Throws on cancel (`NotAllowedError`), no-passkey (404), or
 * any ceremony error — the caller falls back to the normal signing flow.
 */
export async function getSigningAssertion(
  resolutionId: string,
): Promise<AuthenticationResponseJSON> {
  const optionsJSON = await api<PublicKeyCredentialRequestOptionsJSON>(
    `/api/v1/resolutions/${encodeURIComponent(resolutionId)}/signatures/webauthn-options`,
    { method: 'POST' },
  );
  const assertion = await startAuthentication({ optionsJSON });
  return assertion;
}

/** List the caller's own enrolled passkeys. */
export async function listPasskeys(): Promise<PasskeySummary[]> {
  const res = await api<CredentialsListResponse>('/api/v1/auth/webauthn/credentials', {
    useOperatorSession: true,
  });
  return Array.isArray(res?.credentials) ? res.credentials : [];
}

/** Remove one of the caller's own passkeys by its document id. */
export async function removePasskey(id: string): Promise<void> {
  await api(`/api/v1/auth/webauthn/credentials/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    useOperatorSession: true,
  });
}
